import { Injectable, inject } from '@angular/core';
import { TooltipItem } from '@app/core/interfaces/report.interface';
import * as Highcharts from 'highcharts/highstock';
import { FormatService } from './format.service';

@Injectable({
  providedIn: 'root',
})
export class TooltipService {
  private formatService = inject(FormatService);

  // Define common styles as class properties
  private baseFontStyle = `font-size: 1em; line-height: 1.5;`;
  private cellStyle = `padding: 0 5px;`;
  private cellCommonStyle = `${this.cellStyle}; display: flex; align-items: center; justify-content: flex-start;`;
  private valueStyle = `${this.cellStyle}; text-align: right; ${this.baseFontStyle}`;
  private percentageStyle = `${this.cellStyle}; text-align: right; ${this.baseFontStyle} color: #BBBBBB;`;
  private boldStyle = `font-weight: bold;`;
  private categoryHeaderStyle = `display: block; margin-bottom: 6px;`;
  private tableStyle = `width: 100%; border-collapse: collapse;`;
  private greyColor = `#BBBBBB`;

  constructor() {}

  getTooltipFormatter(): Highcharts.TooltipFormatterCallbackFunction {
    return ((component: TooltipService) =>
      function (this: Highcharts.TooltipFormatterContextObject) {
        return component.chartTooltipFormatter(
          this,
          component,
          component.formatService,
        );
      })(this);
  }

  private chartTooltipFormatter(
    context: Highcharts.TooltipFormatterContextObject,
    component: TooltipService,
    formatService: FormatService,
  ): string {
    const chartType = context.series.chart.options.chart.type;
    const pointIndex: number = context.point.index;
    let seriesIndex: number;
    let allPoints: TooltipItem[];

    // Determine series index and point data based on chart type
    if (chartType === 'pie') {
      seriesIndex = pointIndex;
      allPoints = context.series.data.map(
        (point) =>
          ({
            name: point.name,
            y: point.y,
            color: point.color as string,
          }) as TooltipItem,
      );
    } else {
      seriesIndex = context.series.index;
      allPoints = context.series.chart.series.map(
        (series: Highcharts.Series) =>
          ({
            name: series.name,
            y: series.data[pointIndex].y,
            color: series.color as string,
          }) as TooltipItem,
      );
    }

    const point = allPoints[seriesIndex];
    const otherPoints = allPoints.filter(
      (otherPoint) => otherPoint.name !== point.name && otherPoint.y > 0,
    );

    // Calculate grand total
    const grandTotal =
      point.y + otherPoints.reduce((acc, otherPoint) => acc + otherPoint.y, 0);

    // Sort otherPoints by count (y) in descending order
    otherPoints.sort((a, b) => b.y - a.y);

    // Split the top 3 and the rest
    const top3Points = otherPoints.slice(0, 3);
    const otherPointsRest = otherPoints.slice(3);

    // Calculate the sum of the remaining points for "Other"
    const otherPointsValue = otherPointsRest.reduce(
      (acc, otherPoint) => acc + otherPoint.y,
      0,
    );

    // Build category header
    const categoryHeader = context.x
      ? `<b style="${component.categoryHeaderStyle}">${context.x}</b>`
      : ``;

    const isSingleSeries =
      chartType == 'pie' ? false : context.series.chart.series.length == 1;
    const isSingleYAxis = context.series.chart.yAxis.length == 1;

    // Function to generate a row for a given point
    const generateRow = (
      name: string,
      value: number,
      color: string,
      isBold: boolean = false,
      showBullet: boolean = true,
      showPercentage: boolean = true,
    ) => {
      const bulletSpan = showBullet
        ? `<span style="color:${color}; ${component.baseFontStyle}">\u25CF</span>`
        : `<span style="${component.baseFontStyle}">=</span>`;
      const nameStyle = isBold ? component.boldStyle : '';
      const valueText = formatService.formatNumber(value);
      const percentageText = showPercentage
        ? `${formatService.formatNumber((value / grandTotal) * 100)}%`
        : '';

      const percentageRow =
        !isSingleSeries && isSingleYAxis
          ? `<td style="${component.percentageStyle} ${nameStyle}">
            ${percentageText}
          </td>`
          : ``;

      return `
        <tr style="border: none;">
          <td style="${component.cellCommonStyle}">
            ${bulletSpan}
            <span style="margin-left: 5px; ${component.baseFontStyle} ${nameStyle}">${name}</span>
          </td>
          <td style="${component.valueStyle} ${nameStyle}">
            ${valueText}
          </td>
          ${percentageRow}
        </tr>`;
    };

    // Generate the hover item (first row)
    const hoverItem = generateRow(point.name, point.y, point.color, true);

    // Generate the top 3 rows
    const top3Items = top3Points
      .map((otherPoint) =>
        generateRow(otherPoint.name, otherPoint.y, otherPoint.color),
      )
      .join('');

    let otherRow: string;
    // Generate the "Other" row if number of remaining points > 1
    if (otherPointsRest.length > 1) {
      otherRow = generateRow(
        'Other',
        otherPointsValue,
        component.greyColor,
        false,
        true,
      );
      // If only one point remains, do not collapse into 'Other' category
    } else if (otherPointsRest.length > 0) {
      otherRow = generateRow(
        otherPointsRest[0].name,
        otherPointsRest[0].y,
        otherPointsRest[0].color,
        false,
        true,
      );
    } else {
      otherRow = '';
    }

    // Generate the total row (last row) with "= Total"
    const totalRow =
      otherPoints.length > 0 && isSingleYAxis
        ? generateRow('Total', grandTotal, '', true, false, false) // No bullet, no percentage
        : '';

    // Combine everything into the final tooltip content
    const tooltipContent = `
      ${categoryHeader}
      <table style="${component.tableStyle}">
        ${hoverItem}
        ${top3Items}
        ${otherRow}
        ${totalRow}
      </table>`;

    return tooltipContent;
  }
}
